import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Skeleton,
  Alert,
  Pagination,
  Fade
} from '@mui/material';
import {
  Globe,
  Plus,
  ArrowRight,
  Coins,
  History,
  CheckCircle2,
  Clock,
  XCircle,
  Pencil,
  Trash2,
  Eye,
  X,
  MessageCircle,
  Cloud
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { openSite, getDisplayDomain, getCustomDomain } from '../utils/domainUtils';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: '#141926',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  color: '#fff',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.2)',
    '& .MuiCardActions-root': {
      opacity: 1,
      transform: 'translateY(0)',
    },
    '& .MuiIconButton-root': {
      transform: 'translateX(0)',
      opacity: 1,
    }
  }
}));

const TokenCard = styled(Card)(({ theme }) => ({
  background: '#141926',
  color: '#fff',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  position: 'relative',
  overflow: 'hidden',
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 48,
  height: 48,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: color,
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
  }
}));

const StyledCardActions = styled(CardActions)(({ theme }) => ({
  opacity: 0,
  transform: 'translateY(10px)',
  transition: 'all 0.3s ease',
  justifyContent: 'flex-end',
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(255,255,255,0.06)',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  transform: 'translateX(-10px)',
  opacity: 0,
  transition: 'all 0.3s ease',
  color: '#7C3AED',
  '&:hover': {
    background: 'rgba(124, 58, 237, 0.2)',
  }
}));

const StyledTable = styled(TableContainer)(({ theme }) => ({
  background: '#141926',
  borderRadius: '16px',
  border: '1px solid rgba(255,255,255,0.06)',
  overflowX: 'auto',
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(124, 58, 237, 0.1)',
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#7C3AED',
    fontWeight: 600,
  }
}));

const CreateSiteButton = styled(Button)(({ theme }) => ({
  background: '#7C3AED',
  color: '#fff',
  padding: theme.spacing(1.5, 3),
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '1.1rem',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.3)',
    background: '#8B5CF6',
  }
}));

const StatusChip = styled(Chip)(({ status }) => ({
  background: status === 'active' 
    ? 'rgba(16, 185, 129, 0.2)' 
    : status === 'pending' 
    ? 'rgba(245, 158, 11, 0.2)' 
    : 'rgba(239, 68, 68, 0.2)',
  color: status === 'active' 
    ? '#10B981' 
    : status === 'pending' 
    ? '#F59E0B' 
    : '#EF4444',
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: 'inherit'
  }
}));

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [sites, setSites] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [crmBannerDismissed, setCrmBannerDismissed] = useState(
    localStorage.getItem('dismissedCrmApiBanner') === '1'
  );

  const handleDismissCrmBanner = () => {
    localStorage.setItem('dismissedCrmApiBanner', '1');
    setCrmBannerDismissed(true);
  };
  const menuItems = [
    { 
      title: 'Meus Sites', 
      description: 'Gerencie seus sites',
      icon: <Globe size={20} strokeWidth={1.75} />,
      color: '#7C3AED',
      path: '/meus-sites'
    },
    { 
      title: 'Criar Site', 
      description: 'Crie um novo site',
      icon: <Plus size={20} strokeWidth={1.75} />,
      color: '#10B981',
      path: '/ftm-legacy'
    },
    // Card exclusivo de admin: publicação no Cloudflare (.workers.dev).
    ...(user?.tipo === 'admin' ? [{
      title: 'Sites Cloudflare',
      description: 'Publicar no domínio grátis .workers.dev (admin)',
      icon: <Cloud size={20} strokeWidth={1.75} />,
      color: '#F38020',
      path: '/cloudflare-sites'
    }] : [])
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 size={16} strokeWidth={1.75} />;
      case 'pending':
        return <Clock size={16} strokeWidth={1.75} />;
      case 'error':
        return <XCircle size={16} strokeWidth={1.75} />;
      default:
        return null;
    }
  };

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const respUser = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const userData = await respUser.json();
      if (userData.success && userData.user) {
        setUser(userData.user);
        localStorage.setItem('user', JSON.stringify(userData.user));
      }
    } catch (e) {
      // Se falhar, ignora
    }
  };

  useEffect(() => {
    fetchSites();
    fetchUser();
  }, []);

  const fetchSites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sites`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        const sortedSites = [...response.data.sites].sort((a, b) => new Date(b.dataCriacao || b.createdAt) - new Date(a.dataCriacao || a.createdAt));
        setSites(sortedSites);
      }
    } catch (err) {
      setError('Erro ao carregar sites');
    }
    setLoading(false);
  };

  const handleCreateSite = () => {
    navigate('/ftm-legacy');
  };

  const handleEditSite = (siteId) => {
    navigate(`/configuracoes-site/${siteId}`);
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Tem certeza que deseja excluir este site? Esta ação não pode ser desfeita.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/sites/${siteId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setSuccess('Site excluído com sucesso');
        fetchSites();
        fetchUser();
      }
    } catch (err) {
      setError('Erro ao excluir site');
    }
  };

  const handleViewSite = (site) => {
    openSite(site);
  };

  const filteredSites = sites.filter(site => {
    const matchName = site.nomeProjeto?.toLowerCase().includes(search.toLowerCase()) || site.name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter ? (site.status === statusFilter || site.status === (statusFilter === 'ativo' ? 'active' : statusFilter)) : true;
    const matchDate = dateFilter ? (new Date(site.dataCriacao || site.createdAt).toISOString().split('T')[0] === dateFilter) : true;
    return matchName && matchStatus && matchDate;
  });
  const totalPages = Math.ceil(filteredSites.length / rowsPerPage);
  const paginatedSites = filteredSites.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Fade in timeout={400}>
    <Box sx={{ p: { xs: 2, sm: 3 }, background: '#111827', minHeight: '100vh' }}>
      {!crmBannerDismissed && (
        <Box
          onClick={() => navigate('/agendar-verificacao')}
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1.5, sm: 2 },
            p: { xs: 1.75, sm: 2.25 },
            mb: 3,
            borderRadius: 3,
            textDecoration: 'none',
            background: 'linear-gradient(135deg, rgba(37,211,102,0.16) 0%, rgba(124,58,237,0.16) 100%)',
            border: '1px solid rgba(37,211,102,0.35)',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'all 0.25s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 8px 24px rgba(37,211,102,0.18)',
              borderColor: 'rgba(37,211,102,0.6)',
            },
          }}
        >
          <Box sx={{
            flexShrink: 0,
            width: { xs: 36, sm: 44 },
            height: { xs: 36, sm: 44 },
            borderRadius: '50%',
            background: '#25D366',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff',
          }}>
            <MessageCircle size={20} strokeWidth={2} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: { xs: '0.88rem', sm: '0.98rem' }, lineHeight: 1.35, mb: 0.4 }}>
              Quer que eu mesmo verifique a sua BM?
            </Typography>
            <Typography sx={{ color: '#D1D5DB', fontSize: { xs: '0.78rem', sm: '0.85rem' }, lineHeight: 1.4 }}>
              Agende uma verificação manual comigo — seg a sáb, das 09:00 às 17:30. 100% de garantia.
            </Typography>
          </Box>
          <Box sx={{
            display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75,
            flexShrink: 0, color: '#6EE7B7', fontWeight: 600, fontSize: '0.85rem',
          }}>
            Agendar <ArrowRight size={16} />
          </Box>
          <Tooltip title="Dispensar">
            <IconButton
              size="small"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDismissCrmBanner(); }}
              sx={{
                position: { xs: 'absolute', sm: 'static' },
                top: 6, right: 6,
                color: '#9CA3AF',
                '&:hover': { color: '#F9FAFB', background: 'rgba(255,255,255,0.08)' },
              }}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>
          Bem-vindo, {user.nome || 'Usuário'}!
        </Typography>
        <Typography variant="body1" sx={{ color: '#9CA3AF' }}>
          Gerencie seus sites e tokens de forma simples e eficiente.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TokenCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Coins size={32} strokeWidth={1.75} style={{ marginRight: 8 }} />
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    Tokens Disponíveis
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{ fontWeight: 700, ml: 2, background: '#7C3AED' }}
                  onClick={() => navigate('/comprar-tokens')}
                >
                  Comprar Tokens
                </Button>
              </Box>
              <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                {user.tokens || 0}
              </Typography>
              <Box sx={{ mb: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(user.tokens || 0, 100)} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fff',
                    }
                  }} 
                />
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Tokens necessários para criar sites
              </Typography>
            </CardContent>
          </TokenCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff', fontWeight: 600 }}>
                Criar Novo Site
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#9CA3AF' }}>
                Comece a criar seu site agora mesmo com nossos templates exclusivos.
              </Typography>
              <CreateSiteButton
                startIcon={<Plus size={20} strokeWidth={1.75} />}
                onClick={handleCreateSite}
              >
                Criar Novo Site
              </CreateSiteButton>
            </CardContent>
          </StyledCard>
        </Grid>

        {menuItems.map((item) => (
          <Grid item xs={12} sm={6} md={3} key={item.title}>
            <Tooltip title={`Ir para ${item.title}`} arrow>
              <StyledCard>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <IconWrapper color={item.color}>
                    {React.cloneElement(item.icon, { size: 28, strokeWidth: 1.75, style: { color: '#fff' } })}
                  </IconWrapper>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#fff' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
                    {item.description}
                  </Typography>
                </CardContent>
                <StyledCardActions>
                  <StyledIconButton 
                    onClick={() => navigate(item.path)}
                    size="small"
                  >
                    <ArrowRight size={20} strokeWidth={1.75} />
                  </StyledIconButton>
                </StyledCardActions>
              </StyledCard>
            </Tooltip>
          </Grid>
        ))}

        <Grid item xs={12}>
          <StyledCard>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                <History size={20} strokeWidth={1.75} style={{ color: '#7C3AED', marginRight: 8 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                  Histórico de Sites
                </Typography>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Buscar site..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  sx={{ ml: 2, background: '#23263a', borderRadius: 2 }}
                />
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Status"
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                  sx={{ minWidth: 120, background: '#23263a', borderRadius: 2 }}
                  SelectProps={{ native: true }}
                >
                  <option value="">Todos</option>
                  <option value="ativo">Ativo</option>
                  <option value="pending">Pendente</option>
                  <option value="error">Erro</option>
                </TextField>
                <TextField
                  size="small"
                  type="date"
                  variant="outlined"
                  label="Data"
                  InputLabelProps={{ shrink: true }}
                  value={dateFilter}
                  onChange={e => { setDateFilter(e.target.value); setPage(1); }}
                  sx={{ minWidth: 150, background: '#23263a', borderRadius: 2 }}
                />
              </Box>
              <StyledTable>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome do Site</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Última Edição</TableCell>
                      <TableCell>Domínio</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, idx) => (
                        <TableRow key={idx}>
                          {Array.from({ length: 7 }).map((_, i) => (
                            <TableCell key={i}><Skeleton variant="rectangular" height={24} /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : paginatedSites.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ color: '#9CA3AF' }}>
                          Nenhum site encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedSites.map((site, idx) => (
                        <TableRow key={site._id} sx={{ background: idx % 2 === 0 ? 'rgba(124,58,237,0.04)' : 'transparent' }}>
                          <TableCell sx={{ color: '#fff' }}>{site.nomeProjeto || site.name}</TableCell>
                          <TableCell>
                            <StatusChip
                              icon={getStatusIcon(site.status)}
                              label={site.status === 'ativo' || site.status === 'active' ? 'Ativo' : site.status === 'pending' ? 'Pendente' : 'Erro'}
                              status={site.status}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#9CA3AF' }}>{new Date(site.dataCriacao || site.createdAt).toLocaleDateString()}</TableCell>
                          <TableCell sx={{ color: '#9CA3AF' }}>{site.updatedAt ? new Date(site.updatedAt).toLocaleDateString() : '-'}</TableCell>
                          <TableCell sx={{ color: '#9CA3AF' }}>{getDisplayDomain(site)}</TableCell>
                          <TableCell>
                            <Tooltip title="Abrir site" arrow>
                              <IconButton size="small" onClick={() => handleViewSite(site)} sx={{ color: '#7C3AED' }}>
                                <Eye size={20} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copiar link" arrow>
                              <IconButton size="small" onClick={() => navigator.clipboard.writeText(getCustomDomain(site))} sx={{ color: '#7C3AED' }}>
                                <Globe size={20} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Editar" arrow>
                              <IconButton size="small" onClick={() => handleEditSite(site._id)} sx={{ color: '#7C3AED' }}>
                                <Pencil size={20} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir" arrow>
                              <IconButton size="small" onClick={() => handleDeleteSite(site._id)} sx={{ color: '#7C3AED' }}>
                                <Trash2 size={20} strokeWidth={1.75} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </StyledTable>
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} shape="rounded" />
                </Box>
              )}
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </Box>
    </Fade>
  );
};

export default Dashboard;
